import { Button } from "@/components/ui/button";
import { Phone, Menu, X, AlertTriangle, ChevronDown } from "lucide-react";
import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { COMPANY, SERVICE_AREAS } from "@/lib/constants";
import { useVisitorTracking } from "@/hooks/useVisitorTracking";

const SERVICE_DETAIL_LINKS = [
  { label: "Florida Roofing Services", href: "/florida-roofing-services", isNew: true },
  { label: "Residential Roofing", href: "/residential-roofing", isNew: true },
  { label: "Commercial Roofing", href: "/commercial-roofing", isNew: true },
  { label: "Roof Repair", href: "/roof-repair" },
  { label: "Roof Replacement", href: "/roof-replacement" },
  { label: "Metal Roofing", href: "/metal-roofing" },
  { label: "Tile Roofing", href: "/tile-roofing" },
  { label: "Shingle Roofing", href: "/shingle-roofing" },
  { label: "Flat Roofing", href: "/flat-roofing" },
  { label: "Roof Inspection", href: "/roof-inspection" },
  { label: "Insurance Claims", href: "/roof-insurance-claims", isNew: true },
  { label: "All Services", href: "/services" },
];

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [serviceAreasOpen, setServiceAreasOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const location = useLocation();
  const { trackCTAClick, trackPhoneClick } = useVisitorTracking();

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);

  const navLinks = [
    { label: "Home", href: "/" },
    { label: "About", href: "/about" },
    { label: "Services", href: "/services", isDropdown: true, dropdownType: "services" },
    { label: "Projects", href: "/projects" },
    { label: "Before/After", href: "/before-after" },
    { label: "Service Areas", href: "#areas", isDropdown: true, dropdownType: "areas" },
    { label: "Blog", href: "/blog" },
    { label: "Contact", href: "/contact" },
  ];

  const isActive = (path: string) => location.pathname === path;

  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-50 bg-background/95 backdrop-blur-md border-b border-border shadow-soft">
        {/* Top Bar */}
      <div className="bg-primary text-primary-foreground py-2">
        <div className="container mx-auto px-4 flex justify-between items-center text-sm">
          <div className="flex items-center gap-4">
            <span className="hidden sm:inline">Licensed & Insured: {COMPANY.license}</span>
            <span className="hidden md:inline">•</span>
            <span className="hidden md:inline">South Florida's residential &amp; commercial roofing specialists</span>
          </div>
          <a 
            href={COMPANY.phoneHref} 
            className="flex items-center gap-2 font-semibold hover:text-accent transition-colors"
            data-track-cta="header_phone_topbar"
            onClick={() => {
              trackCTAClick('Call Now', 'header_topbar');
              trackPhoneClick('header_topbar');
            }}
          >
            <Phone className="h-4 w-4" />
            <span>Call {COMPANY.phoneFormatted}</span>
          </a>
        </div>
      </div>

      {/* Main Header */}
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo - Optimized WebP with explicit dimensions + aspect-ratio for zero CLS */}
          <Link to="/" className="flex items-center gap-2">
            <img 
              src="/images/logo-112.webp"
              alt="O'Brien Contracting"
              className="h-12 md:h-14 w-12 md:w-14"
              width={56}
              height={56}
              style={{ aspectRatio: "1 / 1" }}
              loading="eager"
              fetchPriority="high"
              decoding="async"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-6">
            {navLinks.map((link) => (
              link.isDropdown ? (
                <div key={link.label} className="relative group">
                  <button className="text-sm font-medium text-foreground hover:text-accent transition-colors flex items-center gap-1">
                    {link.label}
                    <ChevronDown className="w-4 h-4" />
                  </button>
                  <div className="absolute top-full left-0 mt-2 w-56 bg-card rounded-lg shadow-card opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 border border-border z-50">
                    <div className="py-2">
                      {link.dropdownType === "services" ? (
                        SERVICE_DETAIL_LINKS.map((service) => (
                          <Link
                            key={service.href}
                            to={service.href}
                            className="block px-4 py-2 text-sm text-foreground hover:bg-secondary hover:text-accent transition-colors"
                          >
                            {service.label}
                          </Link>
                        ))
                      ) : (
                        <>
                          {SERVICE_AREAS.map((area) => (
                            <Link
                              key={area.slug}
                              to={`/roofing/${area.slug}`}
                              className="block px-4 py-2 text-sm text-foreground hover:bg-secondary hover:text-accent transition-colors"
                            >
                              {area.name}
                            </Link>
                          ))}
                          <Link
                            to="/neighborhoods"
                            className="block px-4 py-2 text-sm font-semibold text-accent border-t border-border hover:bg-secondary transition-colors"
                          >
                            Browse All Neighborhoods →
                          </Link>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <Link
                  key={link.label}
                  to={link.href}
                  className={`text-sm font-medium transition-colors ${
                    isActive(link.href) 
                      ? "text-accent" 
                      : "text-foreground hover:text-accent"
                  }`}
                >
                  {link.label}
                </Link>
              )
            ))}
          </nav>

          {/* Desktop CTAs */}
          <div className="hidden lg:flex items-center gap-3">
            <Link to="/emergency">
              <Button 
                variant="destructive" 
                size="sm"
                className="bg-red-600 hover:bg-red-700 animate-pulse"
                data-track-cta="header_emergency"
                onClick={() => trackCTAClick('Emergency Repair', 'header_desktop')}
              >
                <AlertTriangle className="h-4 w-4 mr-1" />
                Emergency
              </Button>
            </Link>
            <Link to="/quick-estimate">
              <Button 
                variant="outline" 
                size="sm"
                data-track-cta="header_quick_estimate"
                onClick={() => trackCTAClick('Quick Estimate', 'header_desktop')}
              >
                Get Free Roofing Estimate
              </Button>
            </Link>
            <Link to="/leak-detection">
              <Button 
                variant="accent" 
                size="sm"
                data-track-cta="header_leak_detection"
                onClick={() => trackCTAClick('Leak Detection', 'header_desktop')}
              >
                Leak Detection
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Toggle */}
          <button
            className="lg:hidden flex items-center justify-center w-10 h-10 rounded-md bg-secondary hover:bg-secondary/80 transition-colors"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
          >
            {mobileMenuOpen ? (
              <X className="h-6 w-6 text-foreground" />
            ) : (
              <Menu className="h-6 w-6 text-foreground" />
            )}
          </button>
        </div>
      </div>
    </header>

    {/* Mobile Menu - OUTSIDE header for proper z-index stacking */}
    {mobileMenuOpen && (
      <div className="lg:hidden fixed inset-x-0 top-[104px] bottom-0 bg-background border-t border-border z-[60] overflow-y-auto animate-slide-in-down">
        <nav className="container mx-auto px-4 py-4 flex flex-col gap-2">
          {navLinks.filter(l => !l.isDropdown).map((link) => (
            <Link
              key={link.label}
              to={link.href}
              className={`text-base font-medium py-3 transition-colors ${
                isActive(link.href) ? "text-accent" : "text-foreground"
              }`}
              onClick={() => setMobileMenuOpen(false)}
            >
              {link.label}
            </Link>
          ))}
          
          {/* Collapsible Services Dropdown */}
          <div className="py-2">
            <button 
              onClick={() => setServicesOpen(!servicesOpen)}
              className="flex items-center justify-between w-full text-base font-medium text-foreground py-3"
            >
              Services
              <ChevronDown className={`h-5 w-5 transition-transform duration-200 ${servicesOpen ? 'rotate-180' : ''}`} />
            </button>
            
            {servicesOpen && (
              <div className="flex flex-col gap-1 animate-fade-in">
                {SERVICE_DETAIL_LINKS.map((service) => (
                  <Link
                    key={service.href}
                    to={service.href}
                    className="text-base text-muted-foreground py-2 pl-4 hover:text-accent transition-colors"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {service.label}
                  </Link>
                ))}
              </div>
            )}
          </div>

          {/* Collapsible Service Areas Dropdown */}
          <div className="py-2">
            <button 
              onClick={() => setServiceAreasOpen(!serviceAreasOpen)}
              className="flex items-center justify-between w-full text-base font-medium text-foreground py-3"
            >
              Service Areas
              <ChevronDown className={`h-5 w-5 transition-transform duration-200 ${serviceAreasOpen ? 'rotate-180' : ''}`} />
            </button>
            
            {serviceAreasOpen && (
              <div className="flex flex-col gap-1 animate-fade-in">
                {SERVICE_AREAS.map((area) => (
                  <Link
                    key={area.slug}
                    to={`/roofing/${area.slug}`}
                    className="text-base text-muted-foreground py-2 pl-4 hover:text-accent transition-colors"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {area.name}
                  </Link>
                ))}
                <Link
                  to="/neighborhoods"
                  className="text-base font-semibold text-accent py-2 pl-4 hover:underline"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Browse All Neighborhoods →
                </Link>
              </div>
            )}
          </div>

          <div className="flex flex-col gap-2 pt-4 border-t border-border">
            <Link 
              to="/emergency" 
              className="w-full"
              onClick={() => {
                setMobileMenuOpen(false);
                trackCTAClick('Emergency Repair', 'header_mobile');
              }}
            >
              <Button 
                variant="destructive" 
                className="w-full bg-red-600 hover:bg-red-700 animate-pulse pointer-events-none"
                data-track-cta="header_emergency_mobile"
                tabIndex={-1}
              >
                <AlertTriangle className="h-4 w-4 mr-2" />
                Emergency Repair
              </Button>
            </Link>
            <Link 
              to="/quick-estimate" 
              className="w-full"
              onClick={() => {
                setMobileMenuOpen(false);
                trackCTAClick('Quick Estimate', 'header_mobile');
              }}
            >
              <Button 
                variant="outline" 
                className="w-full pointer-events-none"
                data-track-cta="header_quick_estimate_mobile"
                tabIndex={-1}
              >
                Quick Estimate
              </Button>
            </Link>
            <Link 
              to="/leak-detection" 
              className="w-full"
              onClick={() => {
                setMobileMenuOpen(false);
                trackCTAClick('Leak Detection', 'header_mobile');
              }}
            >
              <Button 
                variant="accent" 
                className="w-full pointer-events-none"
                data-track-cta="header_leak_detection_mobile"
                tabIndex={-1}
              >
                Leak Detection
              </Button>
            </Link>
            <a 
              href={COMPANY.phoneHref} 
              className="mt-2 w-full"
              onClick={() => {
                trackCTAClick('Call Now', 'header_mobile');
                trackPhoneClick('header_mobile');
              }}
            >
              <Button 
                variant="default" 
                className="w-full pointer-events-none"
                data-track-cta="header_phone_mobile"
                tabIndex={-1}
              >
                <Phone className="h-4 w-4 mr-2" />
                Call {COMPANY.phoneFormatted}
              </Button>
            </a>
          </div>
          
          {/* Extra padding at bottom for sticky CTA bar */}
          <div className="h-20" />
        </nav>
      </div>
    )}
  </>
  );
};

export default Header;
